import React, { useState, useEffect } from 'react';
import {
    Container,
    Box,
    Typography,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Button,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Chip,
    Alert,
    CircularProgress,
    Grid,
    TableSortLabel,
    InputAdornment,
    TextField,
    Autocomplete,
    Tabs,
    Tab,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    Divider,
    OutlinedInput,
    Checkbox,
    FormControlLabel,
    Switch,
    Tooltip
} from '@mui/material';
import {
    Edit as EditIcon,
    Person as PersonIcon,
    Security as SecurityIcon,
    Search as SearchIcon,
    Clear as ClearIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    ManageAccounts as ManageAccountsIcon,
    Group as GroupIcon
} from '@mui/icons-material';
import {
    getUsers,
    editUser,
    getRoles,
    addRole,
    editRole,
    deleteRole,
    getUserRoles,
    addRoleToUser,
    removeRoleFromUser,
    getVioUsers,
    updateUserVioLink,
    getTelegramUsers,
    updateUserTelegramLink
} from '../http/userApi';
import { setUser } from '../store/reducers/stateReducer';
import { useDispatch } from 'react-redux';

const ManagementPage = () => {
    const dispatch = useDispatch();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    // Данные
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [vioUsers, setVioUsers] = useState([]);
    const [telegramUsers, setTelegramUsers] = useState([]);

    // Поиск и сортировка
    const [userSearch, setUserSearch] = useState('');
    const [userSortField, setUserSortField] = useState('id');
    const [userSortDirection, setUserSortDirection] = useState('asc');

    // Диалоги
    const [openUserDialog, setOpenUserDialog] = useState(false);
    const [openRoleDialog, setOpenRoleDialog] = useState(false);
    const [openRoleManagementDialog, setOpenRoleManagementDialog] = useState(false);

    // Активная вкладка
    const [activeTab, setActiveTab] = useState(0);

    // Формы
    const [userForm, setUserForm] = useState({
        id: null,
        username: '',
        display_name: '',
        mail: '',
        selectedRoles: [], // массив ID ролей
        vio_id: null,
        vio_user: null,
        telegram_id: null,
        telegram_user: null,
        settings: {}
    });

    const [roleForm, setRoleForm] = useState({
        id: null,
        name: '',
        description: ''
    });

    // Загрузка данных
    useEffect(() => {
        loadData();
    }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const [usersData, rolesData, vioUsersData, telegramUsersData] = await Promise.all([
                getUsers(),
                getRoles(),
                getVioUsers(),
                getTelegramUsers()
            ]);
            
            const activeUsers = usersData?.data?.filter(u => u.is_active === true) || [];
            setUsers(activeUsers);
            setRoles(rolesData.data || []);
            setVioUsers(vioUsersData.data || []);
            setTelegramUsers(telegramUsersData.data || []);
        } catch (err) {
            setError('Ошибка загрузки данных: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    // Управление пользователями
    const handleUserSubmit = async () => {
        try {
            setLoading(true);
            
            // Отправляем массив имен ролей
            const roleNames = userForm.selectedRoles.map(roleId => {
                const role = roles.find(r => r.id === roleId);
                return role ? role.name : null;
            }).filter(name => name !== null);

            const updatedUserData = {
                roles: roleNames,
                settings: userForm.settings
            };

            const response = await editUser(userForm.id, updatedUserData);
            
            // Обновляем VIO связь если она изменилась
            if (userForm.vio_id !== null) {
                await updateUserVioLink(userForm.id, userForm.vio_id);
            }

            // Обновляем Telegram связь если она изменилась
            if (userForm.telegram_id !== null) {
                await updateUserTelegramLink(userForm.id, userForm.telegram_id);
            }
            
            // Обновляем пользователя в Redux, если есть данные
            if (response.data) {
                dispatch(setUser(response.data));
            }
            
            setSuccess('Данные пользователя обновлены');
            setOpenUserDialog(false);
            resetUserForm();
            loadData();
        } catch (err) {
            setError('Ошибка обновления данных пользователя: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleUserEdit = async (user) => {
        try {
            // Загружаем роли пользователя
            const userRolesResponse = await getUserRoles(user.id);
            const userRoles = userRolesResponse.data || [];
            const selectedRoleIds = userRoles.map(role => role.id);

            // Находим VIO пользователя по ID
            let vioUser = null;
            if (user.vio_id) {
                vioUser = vioUsers.find(vu => vu.id === user.vio_id);
            }

            // Находим Telegram пользователя по ID
            let telegramUser = null;
            if (user.telegram_id) {
                telegramUser = telegramUsers.find(tu => tu.id === user.telegram_id);
            }
            
            setUserForm({
                id: user.id,
                username: user.username,
                display_name: user.display_name || '',
                mail: user.mail || '',
                selectedRoles: selectedRoleIds,
                vio_id: user.vio_id || null,
                vio_user: vioUser || null,
                telegram_id: user.telegram_id || null,
                telegram_user: telegramUser || null,
                settings: user.settings || {}
            });
            setOpenUserDialog(true);
        } catch (err) {
            setError('Ошибка загрузки ролей пользователя: ' + err.message);
        }
    };

    const resetUserForm = () => {
        setUserForm({
            id: null,
            username: '',
            display_name: '',
            mail: '',
            selectedRoles: [],
            vio_id: null,
            vio_user: null,
            telegram_id: null,
            telegram_user: null,
            settings: {}
        });
    };

    // Управление ролями
    const handleRoleSubmit = async () => {
        try {
            setLoading(true);
            
            if (roleForm.id) {
                // Редактирование существующей роли
                await editRole(roleForm.id, {
                    name: roleForm.name,
                    description: roleForm.description
                });
                setSuccess('Роль обновлена');
            } else {
                // Создание новой роли
                await addRole({
                    name: roleForm.name,
                    description: roleForm.description
                });
                setSuccess('Роль создана');
            }
            
            setOpenRoleDialog(false);
            resetRoleForm();
            loadData();
        } catch (err) {
            setError('Ошибка сохранения роли: ' + err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleRoleEdit = (role) => {
        setRoleForm({
            id: role.id,
            name: role.name,
            description: role.description || ''
        });
        setOpenRoleDialog(true);
    };

    const handleRoleDelete = async (roleId) => {
        if (window.confirm('Вы уверены, что хотите удалить эту роль?')) {
            try {
                setLoading(true);
                await deleteRole(roleId);
                setSuccess('Роль удалена');
                loadData();
            } catch (err) {
                setError('Ошибка удаления роли: ' + err.message);
            } finally {
                setLoading(false);
            }
        }
    };

    const resetRoleForm = () => {
        setRoleForm({
            id: null,
            name: '',
            description: ''
        });
    };

    // Фильтрация и сортировка пользователей
    const filteredAndSortedUsers = () => {
        let filtered = users.filter(user => {
            const searchLower = userSearch.toLowerCase();
            return (
                user.username.toLowerCase().includes(searchLower) ||
                (user.display_name && user.display_name.toLowerCase().includes(searchLower)) ||
                (user.mail && user.mail.toLowerCase().includes(searchLower))
            );
        });

        filtered.sort((a, b) => {
            let aValue = a[userSortField];
            let bValue = b[userSortField];
            
            if (typeof aValue === 'string') {
                aValue = aValue.toLowerCase();
                bValue = bValue.toLowerCase();
            }
            
            if (aValue < bValue) return userSortDirection === 'asc' ? -1 : 1;
            if (aValue > bValue) return userSortDirection === 'asc' ? 1 : -1;
            return 0;
        });

        return filtered;
    };

    const handleUserSort = (field) => {
        if (userSortField === field) {
            setUserSortDirection(userSortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setUserSortField(field);
            setUserSortDirection('asc');
        }
    };

    const clearUserSearch = () => {
        setUserSearch('');
    };

    // Получение ролей пользователя для отображения
    const getUserRolesDisplay = (user) => {
        if (user.roles && Array.isArray(user.roles)) {
            return user.roles;
        }
        // Для обратной совместимости со старой структурой
        if (user.roles && typeof user.roles === 'object') {
            return Object.values(user.roles);
        }
        return [];
    };

    return (
        <Box sx={{ flexGrow: 1, p: 3 }}>
            <Container maxWidth="xl">
                <Typography variant="h4" gutterBottom>
                    Управление пользователями и ролями
                </Typography>
                <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                    Управление пользователями, их ролями и системными настройками
                </Typography>

                {/* Статистика */}
                <Paper sx={{ p: 2, mb: 3 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <PersonIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', lineHeight: 1 }}>
                                        {users.length}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Пользователи
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <SecurityIcon sx={{ fontSize: 24, color: 'warning.main' }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', lineHeight: 1 }}>
                                        {roles.length}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Роли
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <GroupIcon sx={{ fontSize: 24, color: 'success.main' }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', lineHeight: 1 }}>
                                        {users.filter(u => getUserRolesDisplay(u).length > 0).length}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Пользователи с ролями
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                        <Grid item xs={6} sm={3}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <ManageAccountsIcon sx={{ fontSize: 24, color: 'info.main' }} />
                                <Box>
                                    <Typography variant="h6" sx={{ fontSize: '1.1rem', lineHeight: 1 }}>
                                        {users.filter(u => u.vio_id || u.telegram_id).length}
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        Связанные аккаунты
                                    </Typography>
                                </Box>
                            </Box>
                        </Grid>
                    </Grid>
                </Paper>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                        {error}
                    </Alert>
                )}

                {success && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
                        {success}
                    </Alert>
                )}

                {/* Вкладки */}
                <Paper sx={{ width: '100%', mb: 4 }}>
                    <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                        <Tab label="Пользователи" />
                        <Tab label="Роли" />
                    </Tabs>

                    {/* Вкладка пользователей */}
                    {activeTab === 0 && (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6">Пользователи</Typography>
                                <Typography variant="body2" color="textSecondary">
                                    Управление ролями и настройками пользователей
                                </Typography>
                            </Box>

                            {/* Поиск пользователей */}
                            <Box sx={{ mb: 1.5 }}>
                                <TextField
                                    fullWidth
                                    size="small"
                                    variant="outlined"
                                    placeholder="Поиск по имени, email, ролям..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.875rem' } }}
                                    slotProps={{
                                        input: {
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <SearchIcon fontSize="small" />
                                                </InputAdornment>
                                            ),
                                            endAdornment: userSearch && (
                                                <InputAdornment position="end">
                                                    <IconButton onClick={clearUserSearch} size="small">
                                                        <ClearIcon fontSize="small" />
                                                    </IconButton>
                                                </InputAdornment>
                                            )
                                        }
                                    }}
                                />
                            </Box>

                            <TableContainer sx={{
                                maxHeight: '60vh',
                                overflow: 'auto',
                                mb: 3
                            }}>
                                <Table stickyHeader size="small" sx={{
                                    '& .MuiTableCell-root': { py: 1, fontSize: '0.875rem' },
                                    '& .MuiTableHead-root': {
                                        backgroundColor: '#ffffff !important',
                                        zIndex: 1000,
                                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                        borderBottom: '2px solid #e0e0e0'
                                    }
                                }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={userSortField === 'id'}
                                                    direction={userSortField === 'id' ? userSortDirection : 'asc'}
                                                    onClick={() => handleUserSort('id')}
                                                >
                                                    ID
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={userSortField === 'username'}
                                                    direction={userSortField === 'username' ? userSortDirection : 'asc'}
                                                    onClick={() => handleUserSort('username')}
                                                >
                                                    Имя пользователя
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={userSortField === 'display_name'}
                                                    direction={userSortField === 'display_name' ? userSortDirection : 'asc'}
                                                    onClick={() => handleUserSort('display_name')}
                                                >
                                                    Отображаемое имя
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={userSortField === 'mail'}
                                                    direction={userSortField === 'mail' ? userSortDirection : 'asc'}
                                                    onClick={() => handleUserSort('mail')}
                                                >
                                                    Email
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Роли</TableCell>
                                            <TableCell>Привязка к VIO</TableCell>
                                            <TableCell>Привязка к Telegram</TableCell>
                                            <TableCell>Цвет линии</TableCell>
                                            <TableCell>Цвет текста</TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={userSortField === 'created_at'}
                                                    direction={userSortField === 'created_at' ? userSortDirection : 'asc'}
                                                    onClick={() => handleUserSort('created_at')}
                                                >
                                                    Дата создания
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {loading ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center">
                                                    <CircularProgress />
                                                </TableCell>
                                            </TableRow>
                                        ) : filteredAndSortedUsers().length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={10} align="center">
                                                    <Typography variant="body2" color="textSecondary">
                                                        Пользователи не найдены
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredAndSortedUsers().map((user) => (
                                                <TableRow key={user.id}>
                                                    <TableCell>{user.id}</TableCell>
                                                    <TableCell>{user.username}</TableCell>
                                                    <TableCell>{user.display_name || '-'}</TableCell>
                                                    <TableCell>{user.mail || '-'}</TableCell>
                                                    <TableCell>
                                                        {getUserRolesDisplay(user).length > 0 ? (
                                                            getUserRolesDisplay(user).map((roleName, idx) => (
                                                                <Chip
                                                                    key={idx}
                                                                    label={roleName}
                                                                    size="small"
                                                                    variant="outlined"
                                                                    color="primary"
                                                                    sx={{
                                                                        mr: 0.5,
                                                                        mb: 0.5,
                                                                        height: 24,
                                                                        fontSize: '0.75rem'
                                                                    }}
                                                                />
                                                            ))
                                                        ) : (
                                                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                                                Нет ролей
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.vio_id ? (
                                                            <Chip
                                                                label={vioUsers.find(vu => vu.id === user.vio_id)?.fio || `ID: ${user.vio_id}`}
                                                                size="small"
                                                                variant="outlined"
                                                                color="secondary"
                                                                sx={{
                                                                    height: 24,
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                                                Не привязан
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.telegram_id ? (
                                                            <Chip
                                                                label={(() => {
                                                                    const tgUser = telegramUsers.find(tu => tu.id === user.telegram_id);
                                                                    if (tgUser) {
                                                                        return `${tgUser.first_name} ${tgUser.last_name}${tgUser.username ? ` (@${tgUser.username})` : ''}`;
                                                                    }
                                                                    return `ID: ${user.telegram_id}`;
                                                                })()}
                                                                size="small"
                                                                variant="outlined"
                                                                color="info"
                                                                sx={{
                                                                    height: 24,
                                                                    fontSize: '0.75rem'
                                                                }}
                                                            />
                                                        ) : (
                                                            <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                                                                Не привязан
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: user.settings?.line_color || 'transparent', border: '1px solid grey' }} />
                                                            {user.settings?.line_color}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                            <Box sx={{ width: 24, height: 24, borderRadius: 1, bgcolor: user.settings?.text_color || 'transparent', border: '1px solid grey' }} />
                                                            {user.settings?.text_color}
                                                        </Box>
                                                    </TableCell>
                                                    <TableCell>
                                                        {user.created_at ? 
                                                            new Date(user.created_at).toLocaleDateString('ru-RU') : '-'
                                                        }
                                                    </TableCell>
                                                    <TableCell>
                                                        <Tooltip title="Управление ролями и настройками">
                                                            <IconButton 
                                                                onClick={() => handleUserEdit(user)} 
                                                                size="small"
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            {/* Информация о результатах поиска */}
                            {userSearch && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="textSecondary">
                                        Найдено: {filteredAndSortedUsers().length} из {users.length} пользователей
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    )}

                    {/* Вкладка ролей */}
                    {activeTab === 1 && (
                        <Box sx={{ p: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6">Роли</Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => {
                                        resetRoleForm();
                                        setOpenRoleDialog(true);
                                    }}
                                    size="small"
                                >
                                    Добавить роль
                                </Button>
                            </Box>

                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>ID</TableCell>
                                            <TableCell>Название</TableCell>
                                            <TableCell>Описание</TableCell>
                                            <TableCell>Дата создания</TableCell>
                                            <TableCell>Действия</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {roles.map((role) => (
                                            <TableRow key={role.id}>
                                                <TableCell>{role.id}</TableCell>
                                                <TableCell>{role.name}</TableCell>
                                                <TableCell>{role.description || '-'}</TableCell>
                                                <TableCell>
                                                    {role.created_at ? 
                                                        new Date(role.created_at).toLocaleDateString('ru-RU') : '-'
                                                    }
                                                </TableCell>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Tooltip title="Редактировать роль">
                                                            <IconButton 
                                                                onClick={() => handleRoleEdit(role)} 
                                                                size="small"
                                                            >
                                                                <EditIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Удалить роль">
                                                            <IconButton 
                                                                onClick={() => handleRoleDelete(role.id)} 
                                                                size="small"
                                                                color="error"
                                                            >
                                                                <DeleteIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Box>
                    )}
                </Paper>

                {/* Диалог управления пользователем */}
                <Dialog open={openUserDialog} onClose={() => setOpenUserDialog(false)} maxWidth="md" fullWidth>
                    <DialogTitle>
                        Управление пользователем: {userForm.username}
                    </DialogTitle>
                    <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2}}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5}}>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Отображаемое имя:</strong> {userForm.display_name || 'Не указано'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>Email:</strong> {userForm.mail || 'Не указан'}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                                <strong>ID пользователя:</strong> {userForm.id}
                            </Typography>
                        </Box>

                        {/* Множественный выбор ролей */}
                        <FormControl fullWidth>
                            <InputLabel>Роли пользователя</InputLabel>
                            <Select
                                multiple
                                value={userForm.selectedRoles}
                                onChange={(e) => setUserForm({ ...userForm, selectedRoles: e.target.value })}
                                input={<OutlinedInput label="Роли пользователя" />}
                                renderValue={(selected) => (
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                        {selected.map((roleId) => {
                                            const role = roles.find(r => r.id === roleId);
                                            return role ? (
                                                <Chip key={roleId} label={role.name} size="small" />
                                            ) : null;
                                        })}
                                    </Box>
                                )}
                            >
                                {roles.map((role) => (
                                    <MenuItem key={role.id} value={role.id}>
                                        <Checkbox checked={userForm.selectedRoles.indexOf(role.id) > -1} />
                                        <ListItemText 
                                            primary={role.name}
                                            secondary={role.description}
                                        />
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2}}>
                            
                                <TextField
                                    label="Цвет линии"
                                    type="color"
                                    
                                    value={userForm.settings?.line_color || '#ffffff'}
                                    onChange={(e) => setUserForm({ ...userForm, settings: { ...userForm.settings, line_color: e.target.value } })}
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{ 
                                        width: '100px',
                                        '& .MuiInputBase-input': {
                                            padding: '10px 8px'
                                        }
                                    }}
                                />
                                <TextField
                                    label="Цвет текста"
                                    type="color"
                                    value={userForm.settings?.text_color || '#ffffff'}
                                    onChange={(e) => setUserForm({ ...userForm, settings: { ...userForm.settings, text_color: e.target.value } })}
                                    fullWidth
                                    InputLabelProps={{
                                        shrink: true,
                                    }}
                                    sx={{ 
                                        width: '100px',
                                        '& .MuiInputBase-input': {
                                            padding: '10px 8px'
                                        }
                                    }}
                                />
                        </Box>
                        
                        {/* Привязка к VIO */}
                        <FormControl fullWidth>
                            <Autocomplete
                                value={userForm.vio_user}
                                onChange={(event, newValue) => {
                                    setUserForm({ 
                                        ...userForm, 
                                        vio_user: newValue,
                                        vio_id: newValue ? newValue.id : null
                                    });
                                }}
                                options={vioUsers}
                                getOptionLabel={(option) => option.fio || ''}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Привязка к VIO" 
                                        placeholder="Выберите пользователя из системы VIO"
                                        helperText="Связь с пользователем в системе VIO для синхронизации данных"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                        <Box>
                                            <Typography variant="body2">
                                                {option.fio}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {option.id}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                noOptionsText="Пользователи VIO не найдены"
                                clearText="Очистить выбор"
                                openText="Открыть список"
                                closeText="Закрыть список"
                                loadingText="Загрузка..."
                                size="small"
                            />
                        </FormControl>

                        {/* Привязка к Telegram */}
                        <FormControl fullWidth>
                            <Autocomplete
                                value={userForm.telegram_user}
                                onChange={(event, newValue) => {
                                    setUserForm({ 
                                        ...userForm, 
                                        telegram_user: newValue,
                                        telegram_id: newValue ? newValue.id : null
                                    });
                                }}
                                options={telegramUsers}
                                getOptionLabel={(option) => {
                                    if (!option) return '';
                                    return `${option.first_name} ${option.last_name}${option.username ? ` (@${option.username})` : ''}`;
                                }}
                                isOptionEqualToValue={(option, value) => option.id === value?.id}
                                renderInput={(params) => (
                                    <TextField 
                                        {...params} 
                                        label="Привязка к Telegram" 
                                        placeholder="Выберите пользователя из Telegram"
                                        helperText="Связь с пользователем в Telegram для уведомлений"
                                    />
                                )}
                                renderOption={(props, option) => (
                                    <Box component="li" {...props} key={option.id}>
                                        <Box>
                                            <Typography variant="body2">
                                                {option.first_name} {option.last_name}
                                                {option.username && (
                                                    <Typography component="span" variant="body2" color="primary" sx={{ ml: 1 }}>
                                                        @{option.username}
                                                    </Typography>
                                                )}
                                            </Typography>
                                            <Typography variant="caption" color="textSecondary">
                                                ID: {option.id}
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                                noOptionsText="Пользователи Telegram не найдены"
                                clearText="Очистить выбор"
                                openText="Открыть список"
                                closeText="Закрыть список"
                                loadingText="Загрузка..."
                                size="small"
                            />
                        </FormControl>
                        
                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                            Примечание: Пользователь может иметь множество ролей одновременно. Выберите все необходимые роли из списка.
                        </Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenUserDialog(false)}>Отмена</Button>
                        <Button onClick={handleUserSubmit} variant="contained" disabled={loading}>
                            {loading ? <CircularProgress size={20} /> : 'Сохранить'}
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Диалог управления ролями */}
                <Dialog open={openRoleDialog} onClose={() => setOpenRoleDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>
                        {roleForm.id ? 'Редактирование роли' : 'Создание новой роли'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            fullWidth
                            label="Название роли"
                            value={roleForm.name}
                            onChange={(e) => setRoleForm({ ...roleForm, name: e.target.value })}
                            sx={{ mb: 2, mt: 1 }}
                            required
                        />
                        <TextField
                            fullWidth
                            label="Описание роли"
                            value={roleForm.description}
                            onChange={(e) => setRoleForm({ ...roleForm, description: e.target.value })}
                            multiline
                            rows={3}
                            sx={{ mb: 2 }}
                        />
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={() => setOpenRoleDialog(false)}>Отмена</Button>
                        <Button 
                            onClick={handleRoleSubmit} 
                            variant="contained" 
                            disabled={loading || !roleForm.name.trim()}
                        >
                            {loading ? <CircularProgress size={20} /> : (roleForm.id ? 'Обновить' : 'Создать')}
                        </Button>
                    </DialogActions>
                </Dialog>
            </Container>
        </Box>
    );
};

export default ManagementPage; 